import { useEffect, useRef, useState } from 'react';
import { Button } from '../components/Button.jsx';
import { Card } from '../components/Card.jsx';
import { CostBreakdown } from '../components/CostBreakdown.jsx';
import { PortfolioList } from '../components/PortfolioList.jsx';
import { useFocusOnMount } from '../components/useFocusOnMount.js';
import {
  createPortfolioSummary,
  createScorePayload,
  submitScore
} from '../api/scores.js';
import {
  createAnalyticsPayload,
  submitAnalytics
} from '../api/analytics.js';
import { restart } from '../state/actions.js';
import { PAGE } from '../state/pages.js';
import { isAdvancedMode } from '../state/selectors.js';
import { configForState } from '../state/modeConfig.js';
import {
  formatMoney,
  formatPercent,
  formatTwh,
  formatGw
} from '../components/format.js';
import {
  classifyFinalScore,
  scoreRatingBandsForMode
} from './finalScoreRating.js';
import { computePlanRating } from '../engine/planRating.js';
import { useAudio } from '../audio/AudioProvider.jsx';

function pickRandomSeed() {
  return ((Math.random() * 0xffffffff) >>> 0) || 1;
}

function imageForRating(ratingId) {
  if (ratingId === 'excellent') {
    return {
      src: '/Excellent.webp',
      alt: 'Excellent final score'
    };
  }
  if (ratingId === 'strong') {
    return {
      src: '/verygood.webp',
      alt: 'Strong final score'
    };
  }
  if (ratingId === 'costly') {
    return {
      src: '/Angry.webp',
      alt: 'Costly final score'
    };
  }
  if (ratingId === 'high-cost') {
    return {
      src: '/Fired.webp',
      alt: 'High-cost final score'
    };
  }
  return {
    src: '/GoodJob.webp',
    alt: 'Good final score'
  };
}

function cueForRating(ratingId) {
  if (ratingId === 'excellent') return 'fantastic';
  if (ratingId === 'strong') return 'excellentWork';
  if (ratingId === 'competitive') return 'wellDone';
  if (ratingId === 'costly') return 'overspend';
  return 'economyCrashed';
}

export function FinalScoreScreen({
  state,
  dispatch,
  playerName = '',
  playerCountry = '',
  playerRole = '',
  onNavigate
}) {
  const [formulaVisible, setFormulaVisible] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const [planRating, setPlanRating] = useState(null);
  const { playCue } = useAudio();
  const autoSubmitStartedRef = useRef(false);
  const finalCuePlayedRef = useRef(false);
  const awardCuePlayedRef = useRef(false);
  const playCueRef = useRef(playCue);
  const mainRef = useFocusOnMount();

  useEffect(() => {
    playCueRef.current = playCue;
  }, [playCue]);

  useEffect(() => {
    if (!state.finalResult || autoSubmitStartedRef.current) return;

    const trimmedPlayerName = String(playerName ?? '').trim();
    if (!trimmedPlayerName) {
      setSubmitStatus('error');
      setSubmitMessage('Score could not be submitted because the planner ID is blank.');
      return;
    }

    // Both modes submit. createScorePayload carries state.mode, which
    // routes the entry to that mode's own blob key server-side, so the
    // two boards never mix.
    //
    // Best Plans leaderboard ranking needs Plan Rating's expectedCost
    // to ship in the same submission, so wait for it before submitting.
    if (!planRating) return;

    let active = true;
    autoSubmitStartedRef.current = true;
    setSubmitStatus('submitting');
    setSubmitMessage('Submitting score...');

    async function submitCompletedRun() {
      let leaderboardSubmissionStatus = 'unknown';
      try {
        await submitScore(
          createScorePayload({
            state,
            playerName: trimmedPlayerName,
            playerCountry,
            playerRole,
            expectedCost: planRating.expectedCost,
            luckPercentile: planRating.luckPercentile,
            planRatingVersion: planRating.version
          })
        );
        if (!active) return;
        leaderboardSubmissionStatus = 'success';
        setSubmitStatus('success');
        setSubmitMessage('Score submitted.');
        playCueRef.current('leaderboardSubmission');
      } catch (error) {
        leaderboardSubmissionStatus = 'error';
        if (active) {
          setSubmitStatus('error');
          setSubmitMessage(
            error instanceof Error
              ? error.message
              : 'High scores are unavailable right now.'
          );
        }
      }

      try {
        await submitAnalytics(
          createAnalyticsPayload({
            state,
            playerName: trimmedPlayerName,
            playerCountry,
            playerRole,
            leaderboardSubmissionStatus
          })
        );
      } catch {
        // Analytics collection should not interrupt the planner's final score flow.
      }
    }

    submitCompletedRun();

    return () => {
      active = false;
    };
  }, [playerCountry, playerName, playerRole, state, planRating]);

  // Plan Rating (luck-adjusted score): re-resolve the player's exact
  // plan across alternate histories. Deferred off the first paint —
  // the simulation takes a noticeable fraction of a second.
  useEffect(() => {
    if (!state.finalResult) return undefined;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      // No seed is passed: the alternate histories come from
      // PLAN_RATING_SEED, so two players submitting the same plan get
      // the same rating and replaying a plan cannot re-roll it.
      const rating = computePlanRating({
        mode: state.mode,
        buildPlan: state.buildPlanByYear,
        retirePlan: state.retirePlanByYear ?? {},
        realizedCost: state.finalResult.totalCost
      });
      if (!cancelled) setPlanRating(rating);
    }, 50);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [state]);

  useEffect(() => {
    if (!state.finalResult) return;
    if (finalCuePlayedRef.current) return;
    finalCuePlayedRef.current = true;
    playCue(
      cueForRating(classifyFinalScore(state.finalResult.totalCost, state.mode).id)
    );
  }, [playCue, state.finalResult, state.mode]);

  useEffect(() => {
    const result = state.finalResult;
    if (!result) return undefined;
    if (awardCuePlayedRef.current) return;
    if (result.cleanEnergyAward <= 0 && result.resilienceAward <= 0) return;
    awardCuePlayedRef.current = true;
    const timer = window.setTimeout(() => {
      playCue('awardReveal');
    }, 500);
    return () => window.clearTimeout(timer);
  }, [playCue, state.finalResult]);

  const r = state.finalResult;
  if (!r) return null;

  const isAdvanced = isAdvancedMode(state);
  const config = configForState(state);
  const scoreRatingBands = scoreRatingBandsForMode(state.mode);
  const scoreRating = classifyFinalScore(r.totalCost, state.mode);
  const whyRating = [];
  if (r.shortfallCost >= 20) {
    whyRating.push('Shortfall was the main cost driver, adding a large penalty for unmet demand.');
  }
  if (isAdvanced && (r.capacityShortfallCost ?? 0) >= 10) {
    whyRating.push('Firm capacity fell below the reserve-margin requirement, adding peak capacity penalties.');
  }
  if (r.carbonAmount < 0) {
    whyRating.push('Carbon pricing added cost pressure because emissions landed in a penalty band.');
  }
  if (!isAdvanced && r.cleanEnergyAward > 0) {
    whyRating.push('The Clean Energy Transition Award strengthened the final score.');
  }
  if (!isAdvanced && r.resilienceAward > 0) {
    whyRating.push('The System Resilience Award rewarded a broad plant portfolio.');
  }
  if (!isAdvanced && r.cleanEnergyAward === 0 && r.resilienceAward === 0) {
    whyRating.push('No post-Year-3 awards were achieved.');
  }
  if (isAdvanced && (r.resourceDevelopmentPremium ?? 0) > 0) {
    whyRating.push(
      'Repeated use of one exact technology added a resource development premium.'
    );
  }
  const hasHydrogen = state.portfolio.includes('hydrogen');
  const outcomeImage = imageForRating(scoreRating.id);
  const portfolioSummary = createPortfolioSummary(state.portfolio);

  const awardNote = (tierLabel) => (tierLabel ? `(${tierLabel})` : '($0)');

  const rows = [
    {
      label: isAdvanced ? 'Listed capital' : 'Total capital',
      amount: r.capital
    },
    ...(isAdvanced && (r.resourceDevelopmentPremium ?? 0) > 0
      ? [
          {
            label: 'Resource development premium',
            amount: r.resourceDevelopmentPremium,
            note: '(fourth and later player-built copies by exact technology)'
          }
        ]
      : []),
    { label: 'Total operating cost', amount: r.opex },
    ...(isAdvanced && (r.decommissionCost ?? 0) > 0
      ? [
          {
            label: 'Total decommissioning fees',
            amount: r.decommissionCost,
            note: '(plants retired during the game)'
          }
        ]
      : []),
    {
      label: isAdvanced ? 'Total energy shortfall penalty' : 'Total shortfall penalty',
      amount: r.shortfallCost,
      tone: r.shortfallCost > 0 ? 'warning' : null,
      note: r.shortfall > 0 ? `(${formatTwh(r.shortfall)} short across the game)` : null
    },
    ...(isAdvanced
      ? [
          {
            label: 'Total peak capacity penalty',
            amount: r.capacityShortfallCost,
            tone: r.capacityShortfallCost > 0 ? 'warning' : null,
            note:
              r.capacityShortfall > 0
                ? `(${formatGw(r.capacityShortfall)} short across the game)`
                : null
          }
        ]
      : []),
    {
      label: 'Carbon tax / bonus',
      amount: -r.carbonAmount,
      sign: true,
      tone: r.carbonAmount > 0 ? 'clean' : r.carbonAmount < 0 ? 'carbon' : null,
      note: `(${r.bracket.label}, ${state.cop30.headline.toLowerCase()})`
    },
    ...(!isAdvanced
      ? [
          {
            label: 'Clean Energy Transition Award',
            amount: -r.cleanEnergyAward,
            sign: true,
            tone: r.cleanEnergyAward > 0 ? 'clean' : null,
            note: awardNote(r.cleanEnergyAwardTier?.label)
          },
          {
            label: 'System Resilience Award',
            amount: -r.resilienceAward,
            sign: true,
            tone: r.resilienceAward > 0 ? 'clean' : null,
            note: awardNote(r.resilienceAwardTier?.label)
          }
        ]
      : [])
  ];

  return (
    <main ref={mainRef} id="main" tabIndex={-1} className="screen screen-final">
      <Card title="Final score" titleId="final-heading">
        <section
          className={`final-verdict final-verdict-${scoreRating.id}`}
          aria-labelledby="final-verdict-heading"
        >
          <div className="final-verdict-summary">
            <div>
              <p className="final-verdict-kicker">Planning verdict</p>
              <h3 id="final-verdict-heading" className="final-verdict-title">
                {scoreRating.label}
              </h3>
            </div>
            <p className="final-verdict-cost">
              <span>Final cost</span>
              <strong>{formatMoney(r.totalCost)}</strong>
            </p>
          </div>
          <p className="final-verdict-sentence">{scoreRating.sentence}</p>
          <ol className="final-score-band-strip" aria-label="Final cost score bands">
            {scoreRatingBands.map((band) => (
              <li
                key={band.id}
                className={`final-score-band final-score-band-${band.id}${
                  band.id === scoreRating.id ? ' final-score-band-active' : ''
                }`}
                aria-current={band.id === scoreRating.id ? 'true' : undefined}
              >
                <span className="final-score-band-label">{band.label}</span>
                <span className="final-score-band-range">{band.rangeLabel}</span>
              </li>
            ))}
          </ol>
        </section>

        <section
          className="final-rating-reasons final-plan-rating"
          aria-labelledby="final-plan-rating-heading"
          data-testid="final-plan-rating"
        >
          <h3 id="final-plan-rating-heading">Plan rating</h3>
          {planRating ? (
            <>
              <p data-testid="plan-rating-expected">
                Across {planRating.histories} alternate histories of your
                exact plan — fresh disruptions, output rolls, and COP 31
                outcome — the expected cost is{' '}
                <strong>{formatMoney(planRating.expectedCost)}</strong>{' '}
                (typical range {formatMoney(planRating.p10Cost)} to{' '}
                {formatMoney(planRating.p90Cost)}).
              </p>
              <p data-testid="plan-rating-luck">
                Your run cost <strong>{formatMoney(r.totalCost)}</strong> —
                luckier than {formatPercent(planRating.luckPercentile)} of
                histories. This is {planRating.luckBand.article}{' '}
                <strong
                  className={`final-luck-verdict final-luck-${planRating.luckBand.id}`}
                  data-testid="plan-rating-verdict"
                >
                  {planRating.luckBand.label}
                </strong>
                .
              </p>
            </>
          ) : (
            <p data-testid="plan-rating-pending">
              Simulating alternate histories of your plan...
            </p>
          )}
        </section>

        <section className="final-rating-reasons" aria-labelledby="final-rating-reasons-heading">
          <h3 id="final-rating-reasons-heading">Why this rating?</h3>
          <ul role="list">
            {whyRating.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
            {hasHydrogen ? (
              <li>
                Don't believe the hype, Hydrogen is very inefficient (just ask{' '}
                <a
                  className="final-disguised-link"
                  href="https://doi.org/10.1038/s44359-025-00121-6"
                  target="_blank"
                  rel="noreferrer"
                >
                  Nathan
                </a>
                !)
              </li>
            ) : null}
          </ul>
        </section>

        <img
          className="final-outcome-image"
          src={outcomeImage.src}
          alt={outcomeImage.alt}
          loading="eager"
          decoding="async"
        />

        <CostBreakdown rows={rows} total={r.totalCost} totalLabel="Final total cost" />
        <div className="final-formula-reveal">
          <button
            type="button"
            className="final-formula-toggle"
            aria-expanded={formulaVisible}
            aria-controls="final-formula-text"
            onClick={() => setFormulaVisible((visible) => !visible)}
          >
            Formulae
          </button>
          {formulaVisible ? (
            <p id="final-formula-text" className="card-body final-formula-text">
              {isAdvanced
                ? 'Formula: listed capital + resource development premiums + opex + decommissioning fees + energy shortfall penalty + peak capacity penalty - COP 31 Antalya carbon amount. Advanced has no post-game awards. Negative emissions receive credit only for post-disaster clean generation needed to meet annual demand.'
                : 'Formula: capital + opex + shortfall penalty - COP 31 Antalya carbon amount - clean award - resilience award. Both awards are flat post-Year-3 amounts based on build decisions and are not multiplied by the COP 31 outcome.'}
            </p>
          ) : null}
        </div>

        <h3 className="final-subheading">Highlights</h3>
        <ul className="final-highlights" role="list">
          <li>
            Three-year output: {formatTwh(r.totalOutput)}{' '}
            (clean energy share {formatPercent(r.cleanEnergyShare)}, variable clean share{' '}
            {formatPercent(r.variableCleanEnergyShare)}).
          </li>
          {!isAdvanced ? (
            <>
              <li>
                Clean Energy Transition Award: {formatMoney(r.cleanEnergyAward)}
                {' '}from post-disaster clean TWh.
              </li>
              <li>
                System Resilience Award: {formatMoney(r.resilienceAward)} from{' '}
                {r.resilienceFamilies} families built, largest family share{' '}
                {formatPercent(r.resilienceLargestFamilyShare)}.
              </li>
            </>
          ) : null}
          {isAdvanced && (r.resourceDevelopmentPremium ?? 0) > 0 ? (
            <li>
              Resource development premium:{' '}
              {formatMoney(r.resourceDevelopmentPremium)}.
            </li>
          ) : null}
          <li>Total emissions: {r.emissions}, bracket {r.bracket.label}.</li>
          {isAdvanced ? (
            <li>
              Carbon-scoring emissions after useful-generation credit and
              yearly weighting: {r.weightedEmissions}.
            </li>
          ) : null}
          <li>
            COP 31 Antalya: {state.cop30.headline}. Bonus x{state.cop30.bonusMultiplier},
            penalty x{state.cop30.penaltyMultiplier}.
          </li>
        </ul>

        <h3 className="final-subheading">Your final portfolio</h3>
        <PortfolioList portfolio={state.portfolio} plantsById={config.plantsById} />

        <section
          className="final-score-submit"
          aria-labelledby="final-score-submit-heading"
        >
          <h3 id="final-score-submit-heading" className="final-subheading">
            High scores
          </h3>
          <dl className="final-score-submit-meta">
            <div>
              <dt>Planner</dt>
              <dd>{String(playerName ?? '').trim() || 'Unrecorded'}</dd>
            </div>
            {isAdvanced ? (
              <div>
                <dt>Mode</dt>
                <dd>Advanced (beta)</dd>
              </div>
            ) : null}
            <div>
              <dt>Cost</dt>
              <dd>{formatMoney(r.totalCost)}</dd>
            </div>
            <div>
              <dt>COP 31 Antalya</dt>
              <dd>{state.cop30?.headline ?? 'Unknown'}</dd>
            </div>
          </dl>
          <p className="final-score-submit-summary">{portfolioSummary}</p>
          {submitMessage ? (
            <p
              className={`final-score-submit-message final-score-submit-message-${submitStatus}`}
              role={submitStatus === 'error' ? 'alert' : 'status'}
            >
              {submitMessage}
            </p>
          ) : null}
          <div className="final-score-submit-actions">
            <Button
              onClick={() => onNavigate?.(PAGE.DAILY_HIGH_SCORES, { returnTo: PAGE.GAME })}
              changesScreen
              testId="final-daily-high-scores"
            >
              Daily High Scores
            </Button>
            <Button
              variant="secondary"
              onClick={() => onNavigate?.(PAGE.ALL_TIME_HIGH_SCORES, { returnTo: PAGE.GAME })}
              changesScreen
              testId="final-all-time-high-scores"
            >
              All-Time High Scores
            </Button>
          </div>
        </section>

        <div className="screen-actions">
          <Button
            onClick={() => dispatch(restart(pickRandomSeed()))}
            changesScreen
            testId="final-restart"
          >
            Play again
          </Button>
        </div>
      </Card>
    </main>
  );
}
